import { getAccessibleContrastColor } from "./editions";

const HOMEPAGE_ACTION_LABEL_SELECTOR = [
    ".homepage-refresh__hero-read-label",
    ".homepage-refresh__rail-card-read",
    ".homepage-refresh__impact-card-watch",
    ".homepage-refresh__coverage-card-read",
].join(",");

function syncHomepageActionContrast(root = document) {
    root.querySelectorAll(HOMEPAGE_ACTION_LABEL_SELECTOR).forEach(label => {
        const backgroundColor = window.getComputedStyle(label).backgroundColor;
        label.style.color = getAccessibleContrastColor(backgroundColor);
    });
}

function activateEdition(shell, key) {
    const currentButton = shell.querySelector("[data-homepage-edition-current]");
    const optionButtons = Array.from(shell.querySelectorAll("[data-homepage-edition-option]"));
    const activePanel = shell.querySelector(`[data-edition-primary-panel][data-edition-key="${key}"]`);
    const currentEditionKey = shell.dataset.currentEditionKey || "";
    const isSiteCurrent = key === currentEditionKey;
    const filter = shell.querySelector("[data-homepage-edition-filter]");
    const isOpen = filter?.classList.contains("is-open");

    optionButtons.forEach(button => {
        const isActive = button.dataset.editionKey === key;
        button.classList.toggle("is-hidden", isActive);
        button.classList.toggle("is-visible", !isActive && isOpen);
    });

    if (activePanel && currentButton) {
        const accent = activePanel.dataset.editionAccent || "#cd6cb1";
        currentButton.querySelector(".homepage-refresh__edition-filter-current-name").textContent = activePanel.dataset.editionName || "";
        currentButton.style.setProperty("--homepage-refresh-accent", accent);
        currentButton.style.setProperty("--edition-filter-contrast", getAccessibleContrastColor(accent));
        shell.style.setProperty("--homepage-refresh-accent", accent);

        const heroToolbar = document.querySelector("[data-homepage-hero-toolbar]");
        if (heroToolbar) {
            heroToolbar.style.setProperty("--homepage-refresh-accent", accent);
        }
    }

    const headingLabel = shell.querySelector("[data-edition-heading-label]");
    const headingLink = shell.querySelector("[data-edition-heading-link]");
    const headingName = shell.querySelector("[data-edition-heading-name]");

    if (activePanel && headingLabel && headingLink && headingName) {
        const accent = activePanel.dataset.editionAccent || "#cd6cb1";
        headingLabel.textContent = isSiteCurrent ? "Current Edition" : "Edition";
        headingLink.href = activePanel.dataset.editionUrl || "#";
        headingName.textContent = activePanel.dataset.editionName || "";
        headingLink.style.setProperty("--homepage-refresh-accent", accent);
        headingLink.style.color = accent;
    }

    shell.querySelectorAll("[data-edition-primary-panel]").forEach(panel => {
        panel.classList.toggle("is-active", panel.dataset.editionKey === key);
    });

    shell.querySelectorAll("[data-edition-rail-panel]").forEach(panel => {
        panel.classList.toggle("is-active", panel.dataset.editionKey === key);
    });

    shell.dataset.activeEdition = key;
    syncHomepageActionContrast(shell);
}

function syncOptionVisibility(shell) {
    const filter = shell.querySelector("[data-homepage-edition-filter]");
    const isOpen = filter?.classList.contains("is-open");
    const activeKey = shell.dataset.activeEdition;

    shell.querySelectorAll("[data-homepage-edition-option]").forEach(button => {
        const isActive = button.dataset.editionKey === activeKey;
        button.classList.toggle("is-hidden", isActive);
        button.classList.toggle("is-visible", Boolean(isOpen) && !isActive);
    });
}

function bindEditionShell(shell) {
    if (shell.dataset.editionShellBound === "1") return;

    const filter = shell.querySelector("[data-homepage-edition-filter]");
    const toggle = shell.querySelector("[data-homepage-edition-toggle]");
    const options = Array.from(shell.querySelectorAll("[data-homepage-edition-option]"));

    if (toggle && filter) {
        toggle.addEventListener("click", () => {
            const isOpen = filter.classList.toggle("is-open");
            toggle.setAttribute("aria-expanded", String(isOpen));
            syncOptionVisibility(shell);
        });
    }

    options.forEach(button => {
        const accent = button.dataset.editionAccent || "#cd6cb1";
        button.style.setProperty("--edition-filter-contrast", getAccessibleContrastColor(accent));

        button.addEventListener("click", () => {
            activateEdition(shell, button.dataset.editionKey || "");
            if (filter && toggle) {
                filter.classList.remove("is-open");
                toggle.setAttribute("aria-expanded", "false");
            }
            syncOptionVisibility(shell);
        });
    });

    activateEdition(shell, shell.dataset.activeEdition || "");
    syncOptionVisibility(shell);
    shell.dataset.editionShellBound = "1";
}

const COVERAGE_LINKS_BREAKPOINT = 1200;

function bindCoverageLinksHeight(grid) {
    if (grid.dataset.coverageLinksHeightBound === "1") return;

    const copy = grid.querySelector(".homepage-refresh__coverage-copy");
    const links = grid.querySelector(".homepage-refresh__coverage-links");
    if (!copy || !links) return;

    const sync = () => {
        if (window.innerWidth <= COVERAGE_LINKS_BREAKPOINT) {
            links.style.removeProperty("--coverage-links-height");
            return;
        }

        links.style.setProperty("--coverage-links-height", `${copy.offsetHeight}px`);
    };

    const observer = new ResizeObserver(sync);
    observer.observe(copy);
    window.addEventListener("resize", sync);
    sync();
    grid.dataset.coverageLinksHeightBound = "1";
}

function getSliderGap(track) {
    const trackStyles = window.getComputedStyle(track);
    return Number.parseFloat(trackStyles.columnGap || trackStyles.gap || "0") || 0;
}

function getSliderStep(viewport, track, slides) {
    const gap = getSliderGap(track);
    const slideWidth = slides[0]?.getBoundingClientRect().width || 0;

    if (slideWidth > 0) {
        return slideWidth + gap;
    }

    const viewportWidth = viewport?.clientWidth || track.clientWidth || 0;
    return viewportWidth > 0 ? viewportWidth + gap : 0;
}

function bindHorizontalSlider(section, config) {
    const boundKey = config.boundKey;
    if (section.dataset[boundKey] === "1") return;

    const track = section.querySelector(config.trackSelector);
    const slides = Array.from(section.querySelectorAll(config.slideSelector));
    const viewport = section.querySelector(config.viewportSelector) || track?.parentElement;
    const prev = section.querySelector(config.prevSelector);
    const next = section.querySelector(config.nextSelector);

    if (!track || slides.length <= 1 || !prev || !next || !viewport) {
        section.dataset[boundKey] = "1";
        return;
    }

    let index = 0;

    const update = () => {
        const visibleCount = config.getVisibleCount();
        const maxIndex = Math.max(0, slides.length - visibleCount);
        const viewportWidth = viewport.clientWidth || 0;

        if (viewportWidth > 0) {
            viewport.style.setProperty("--homepage-slider-slide-width", `${viewportWidth}px`);
        }

        const step = getSliderStep(viewport, track, slides);

        if (index > maxIndex) index = maxIndex;

        track.style.transform = step > 0 ? `translate3d(-${step * index}px, 0, 0)` : "";
        prev.disabled = index === 0;
        next.disabled = index >= maxIndex;
    };

    prev.addEventListener("click", () => {
        index = Math.max(0, index - 1);
        update();
    });

    next.addEventListener("click", () => {
        const maxIndex = Math.max(0, slides.length - config.getVisibleCount());
        index = Math.min(maxIndex, index + 1);
        update();
    });

    window.addEventListener("resize", update);
    slides.forEach(slide => {
        slide.querySelectorAll("img").forEach(img => {
            if (!img.complete) {
                img.addEventListener("load", update, { once: true });
            }
        });
    });

    update();
    section.dataset[boundKey] = "1";
}

function bindCoverageSlider(section) {
    bindHorizontalSlider(section, {
        boundKey: "coverageSliderBound",
        trackSelector: "[data-homepage-coverage-track]",
        slideSelector: "[data-homepage-coverage-slide]",
        viewportSelector: ".homepage-refresh__coverage-slider-viewport",
        prevSelector: "[data-homepage-coverage-prev]",
        nextSelector: "[data-homepage-coverage-next]",
        getVisibleCount: () => (window.innerWidth <= 767 ? 1 : 3),
    });
}

function bindImpactSlider(section) {
    bindHorizontalSlider(section, {
        boundKey: "impactSliderBound",
        trackSelector: "[data-homepage-impact-track]",
        slideSelector: "[data-homepage-impact-slide]",
        viewportSelector: ".homepage-refresh__impact-slider-viewport",
        prevSelector: "[data-homepage-impact-prev]",
        nextSelector: "[data-homepage-impact-next]",
        getVisibleCount: () => {
            if (window.innerWidth <= 767) return 1;
            if (window.innerWidth <= 1200) return 2;
            return 3;
        },
    });
}

function bindNewsletterPicker(section) {
    if (section.dataset.newsletterPickerBound === "1") return;

    const options = Array.from(section.querySelectorAll("[data-newsletter-option]"));
    const panelToggle = section.querySelector("[data-newsletter-panel-toggle]");
    const panel = section.querySelector("[data-newsletter-panel]");
    const panelClose = section.querySelector("[data-newsletter-panel-close]");
    const formBarClose = section.querySelector("[data-newsletter-form-close]");
    const openLabel = section.querySelector("[data-newsletter-open-label]");
    const formBar = section.querySelector("[data-newsletter-form-bar]");
    const countEl = section.querySelector("[data-newsletter-count]");
    const hiddenInputs = section.querySelector("[data-newsletter-hidden-inputs]");
    const form = section.querySelector("[data-members-form='subscribe']");
    const toast = section.querySelector("[data-newsletter-toast]");

    if (!options.length || !panelToggle || !panel || !panelClose || !openLabel || !formBar || !formBarClose || !countEl || !hiddenInputs || !form) {
        section.dataset.newsletterPickerBound = "1";
        return;
    }

    let formBarDismissed = false;
    let toastTimeoutId = 0;
    let hadSuccess = false;
    let hadError = false;

    const getSelectedOptions = () => options.filter(option => option.classList.contains("is-selected"));

    const setPanelOpen = isOpen => {
        section.classList.toggle("is-panel-open", isOpen);
        panel.hidden = !isOpen;
        panelToggle.hidden = isOpen;
        panelToggle.setAttribute("aria-expanded", String(isOpen));
    };

    const syncFormBarVisibility = () => {
        const selected = getSelectedOptions();
        const count = selected.length;
        const subscribed = form.classList.contains("success");
        const showFormBar = count > 0 && !formBarDismissed && !subscribed;

        countEl.textContent = String(count).padStart(2, "0");
        openLabel.textContent = count ? `[${String(count).padStart(2, "0")}]` : "[Select]";
        formBar.hidden = !showFormBar;
        section.classList.toggle("has-newsletter-selection", showFormBar);
        document.body.classList.toggle("has-newsletter-sticky-bar", showFormBar);

        return count;
    };

    const syncSelection = () => {
        const count = syncFormBarVisibility();

        options.forEach(option => {
            const isSelected = option.classList.contains("is-selected");
            const state = option.querySelector("[data-newsletter-option-state]");
            if (state) {
                state.textContent = isSelected ? "Selected" : "Select";
            }
        });

        hiddenInputs.innerHTML = getSelectedOptions()
            .map(option => `<input type="hidden" data-members-newsletter value="${option.dataset.newsletterValue || ""}">`)
            .join("");

        if (count === 0) {
            formBarDismissed = false;
        }
    };

    const syncSuccessState = () => {
        const subscribed = form.classList.contains("success");
        section.classList.toggle("is-subscribe-success", subscribed);
        if (subscribed) {
            formBarDismissed = true;
        }
        syncFormBarVisibility();
    };

    const hideToast = () => {
        if (!toast) return;
        toast.hidden = true;
        toast.textContent = "";
        delete toast.dataset.state;
    };

    const showToast = (message, state = "success") => {
        if (!toast || !message) return;
        toast.textContent = message;
        toast.dataset.state = state;
        toast.hidden = false;
        window.clearTimeout(toastTimeoutId);
        toastTimeoutId = window.setTimeout(hideToast, 3600);
    };

    const syncFormStateFeedback = () => {
        const hasSuccess = form.classList.contains("success");
        const hasError = form.classList.contains("error");

        if (hasSuccess && !hadSuccess) {
            showToast("Subscribed. Please check your inbox to confirm.", "success");
        }

        if (hasError && !hadError) {
            const errorMessage = form.querySelector("[data-members-error]")?.textContent?.trim() || "Could not subscribe. Please try again.";
            showToast(errorMessage, "error");
        }

        hadSuccess = hasSuccess;
        hadError = hasError;
    };

    options.forEach(option => {
        option.addEventListener("click", () => {
            option.classList.toggle("is-selected");
            syncSelection();
        });
    });

    panelToggle.addEventListener("click", () => setPanelOpen(true));
    panelClose.addEventListener("click", () => setPanelOpen(false));
    formBarClose.addEventListener("click", () => {
        formBarDismissed = true;
        syncFormBarVisibility();
    });

    window.addEventListener("keydown", event => {
        if (event.key === "Escape" && section.classList.contains("is-panel-open")) {
            setPanelOpen(false);
        }
    });

    new MutationObserver(() => {
        syncSuccessState();
        syncFormStateFeedback();
    }).observe(form, { attributes: true, attributeFilter: ["class"] });

    setPanelOpen(false);
    syncSelection();
    syncSuccessState();
    syncFormStateFeedback();
    section.dataset.newsletterPickerBound = "1";
}

function bindHeroShare(toolbar) {
    if (toolbar.dataset.heroShareBound === "1") return;

    const shareTrigger = toolbar.querySelector("[data-homepage-hero-share-trigger]");
    const sharePanel = toolbar.querySelector("[data-homepage-hero-share-panel]");
    const shareClose = toolbar.querySelector("[data-homepage-hero-share-close]");
    const shareUrl = toolbar.dataset.shareUrl;
    const shareTitle = toolbar.dataset.shareTitle || document.title;

    const setShareOpen = open => {
        toolbar.classList.toggle("is-share-open", open);
        if (sharePanel) {
            sharePanel.hidden = !open;
        }
        if (shareTrigger) {
            shareTrigger.setAttribute("aria-expanded", String(open));
            shareTrigger.hidden = open;
        }
    };

    const shareNative = async () => {
        if (!shareUrl) return;

        try {
            if (navigator.share) {
                await navigator.share({ title: shareTitle, url: shareUrl });
                return;
            }

            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(shareUrl);
            }
        } catch (error) {
            console.error(error);
        }
    };

    setShareOpen(false);

    if (shareTrigger) {
        shareTrigger.addEventListener("click", event => {
            event.preventDefault();
            event.stopPropagation();
            setShareOpen(true);
        });
    }

    if (shareClose) {
        shareClose.addEventListener("click", event => {
            event.preventDefault();
            event.stopPropagation();
            setShareOpen(false);
        });
    }

    document.addEventListener("click", event => {
        if (!toolbar.contains(event.target)) {
            setShareOpen(false);
        }
    });

    document.addEventListener("keydown", event => {
        if (event.key === "Escape") {
            setShareOpen(false);
        }
    });

    toolbar.querySelectorAll("[data-share-button]").forEach(button => {
        button.addEventListener("click", async event => {
            event.preventDefault();
            event.stopPropagation();

            if (!shareUrl) return;

            const network = button.dataset.shareButton;
            let intentUrl = "";

            if (network === "linkedin") {
                intentUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
            } else if (network === "facebook") {
                intentUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
            } else if (network === "x") {
                intentUrl = `https://x.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
            } else if (network === "bluesky") {
                intentUrl = `https://bsky.app/intent/compose?text=${encodeURIComponent(`${shareTitle} ${shareUrl}`)}`;
            }

            if (intentUrl) {
                window.open(intentUrl, "_blank", "noopener,noreferrer,width=640,height=720");
                return;
            }

            await shareNative();
        });
    });

    toolbar.dataset.heroShareBound = "1";
}

export default function initHomepageRefresh(root = document) {
    root.querySelectorAll("[data-homepage-edition-shell]").forEach(bindEditionShell);
    root.querySelectorAll("[data-homepage-coverage-grid]").forEach(bindCoverageLinksHeight);
    root.querySelectorAll("[data-homepage-coverage-slider]").forEach(bindCoverageSlider);
    root.querySelectorAll("[data-homepage-impact-slider]").forEach(bindImpactSlider);
    root.querySelectorAll("[data-homepage-newsletter-picker]").forEach(bindNewsletterPicker);
    root.querySelectorAll("[data-homepage-hero-toolbar]").forEach(bindHeroShare);
    syncHomepageActionContrast(root);
}
